import debounce from "debounce";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Shortcut } from "./shortcut";

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
  subLabel?: string;
}

export interface AsyncComboBoxProps<T> {
  /** Async function to fetch options */
  fetcher: (query?: string) => Promise<T[]>;
  /** Function to resolve the value when it changes externally */
  resolveValue: (value: string) => Promise<T | null>;
  /** Function to render each option */
  renderOption?: (option: T) => React.ReactNode; // Make this optional
  /** Function to get the value from an option */
  getOptionValue: (option: T) => string;
  /** Function to get the label from an option (required if renderOption is not provided) */
  getOptionLabel?: (option: T) => string;
  /** Function to get the subLabel from an option (optional for default renderer) */
  getOptionSubLabel?: (option: T) => string | undefined;
  /** Function to get the display value for the selected option */
  getDisplayValue?: (option: T) => React.ReactNode;
  /** Custom not found message */
  notFound?: React.ReactNode;
  /** Custom loading skeleton */
  loadingSkeleton?: React.ReactNode;
  /** Currently selected value */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Label for the select field */
  label?: string;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Disable the entire select */
  disabled?: boolean;
  /** Read only the entire select */
  readOnly?: boolean;
  /** Custom width for the popover */
  width?: string | number;
  /** Custom class names */
  className?: string;
  /** Custom trigger button class names */
  triggerClassName?: string;
  /** Custom no results message */
  noResultsMessage?: string;
  /** Auto focus the input */
  autoFocus?: boolean;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Custom label for the search input */
  variant?: "ghost" | "outline";
  /** Custom on key down */
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement | HTMLInputElement>) => void;
  /** Async callback to fetch more items when user scrolls to bottom - should return new items to append, or empty array when no more data */
  onFetchMore?: (searchTerm?: string) => Promise<T[]>;
  /** Whether there's more data to fetch */
  hasMore?: boolean;
  /** Debounce delay for backend search in ms */
  searchDebounceMs?: number;
}

export function AsyncComboBox<T>({
  fetcher,
  renderOption,
  getOptionValue,
  getOptionLabel,
  getOptionSubLabel,
  getDisplayValue,
  autoFocus = false,
  onKeyDown,
  notFound,
  loadingSkeleton,
  label = "",
  placeholder = "",
  value,
  onChange,
  disabled = false,
  readOnly = false,
  width = "100%",
  className,
  triggerClassName,
  noResultsMessage,
  clearable = true,
  variant = "outline",
  resolveValue,
  onFetchMore,
  hasMore = true,
  searchDebounceMs = 300,
}: AsyncComboBoxProps<T>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const isInitialized = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const comboBoxId = useId();
  const [highlightedValue, setHighlightedValue] = useState<string>("-");
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const resolvedValueRef = useRef<string | null>(null);
  const fetcherRef = useRef(fetcher);
  const resolveValueRef = useRef(resolveValue);
  const getOptionValueRef = useRef(getOptionValue);
  const initialValueRef = useRef(value);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    resolveValueRef.current = resolveValue;
  }, [resolveValue]);

  useEffect(() => {
    getOptionValueRef.current = getOptionValue;
  }, [getOptionValue]);

  const { t } = useTranslation();

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus && triggerRef.current) {
      triggerRef.current.click();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    } else {
      setReachedEnd(false);
    }
  }, [open]);

  useEffect(() => {
    if (value && options.length > 0) {
      const option = options.find(
        (opt) => getOptionValueRef.current(opt) === value,
      );
      if (option) {
        setSelectedOption(option);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  useEffect(() => {
    const preloadOptions = async () => {
      const initialValue = initialValueRef.current;
      try {
        setLoading(true);
        setError(null);

        const [data, resolved] = await Promise.all([
          fetcherRef.current(""),
          initialValue
            ? resolveValueRef.current(initialValue)
            : Promise.resolve(null),
        ]);

        setOptions(data);

        if (resolved) {
          setSelectedOption(resolved);
          resolvedValueRef.current = initialValue;
        } else if (initialValue) {
          const found = data.find(
            (opt) => getOptionValueRef.current(opt) === initialValue,
          );
          if (found) {
            setSelectedOption(found);
            resolvedValueRef.current = initialValue;
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch options",
        );
      } finally {
        setLoading(false);
        isInitialized.current = true;
      }
    };

    preloadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialized.current) return;

    const shouldResolve =
      value &&
      value !== resolvedValueRef.current &&
      !options.some((opt) => getOptionValueRef.current(opt) === value);

    if (!shouldResolve) return;

    const resolveInitialValue = async () => {
      try {
        setLoading(true);
        setError(null);

        const resolved = await resolveValueRef.current(value);

        if (resolved) {
          setSelectedOption(resolved);
          resolvedValueRef.current = value;
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch options",
        );
      } finally {
        setLoading(false);
      }
    };

    resolveInitialValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (!isInitialized.current) return;

        try {
          setIsSearching(true);
          setError(null);
          const data = await fetcherRef.current(term);
          setOptions(data);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to fetch options",
          );
        } finally {
          setIsSearching(false);
        }
      }, searchDebounceMs),
    [searchDebounceMs],
  );

  useEffect(() => {
    if (!isInitialized.current) return;

    setReachedEnd(false);

    debouncedSearch(searchTerm);

    return () => {
      debouncedSearch.clear();
    };
  }, [searchTerm, debouncedSearch]);

  const handleSelect = useCallback(
    (currentValue: string) => {
      const newValue =
        clearable && currentValue === selectedValue ? "" : currentValue;

      setSelectedValue(newValue);
      const option =
        options.find((opt) => getOptionValueRef.current(opt) === newValue) ||
        null;
      setSelectedOption(option);
      resolvedValueRef.current = newValue;
      onChange(newValue);
      setOpen(false);
    },
    [selectedValue, onChange, clearable, options],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      //@ts-ignore hackity hack to ignore keyboard enter and only use programmatic enter
      if (event.key === "Enter" && event.nativeEvent.isTrusted) {
        setTimeout(() => {
          onKeyDown?.(event);
        }, 100);
        return;
      }

      if (!open) {
        setOpen(true);
      }

      if (event.key === "Escape") {
        input.blur();
      }

      onKeyDown?.(event);
    },
    [open, onKeyDown],
  );

  useEffect(() => {
    if (options.length > 0 && open) {
      if (searchTerm) {
        const firstValue = getOptionValueRef.current(options[0]);
        setHighlightedValue(firstValue);
      } else {
        setHighlightedValue("-");
      }
    } else {
      setHighlightedValue("-");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, open, searchTerm]);

  useEffect(() => {
    if (!open || !onFetchMore) return;

    const rafId = requestAnimationFrame(() => {
      const listElement = listRef.current;
      if (!listElement) return;

      const handleScroll = async () => {
        if (isFetchingMore || !hasMore || reachedEnd) return;

        const { scrollTop, scrollHeight, clientHeight } = listElement;
        const scrollThreshold = 50;

        if (scrollTop + clientHeight >= scrollHeight - scrollThreshold) {
          setIsFetchingMore(true);
          try {
            const newItems = await onFetchMore(searchTerm || undefined);

            if (newItems.length === 0) {
              setReachedEnd(true);
              return;
            }

            const dedupeAndAppend = (prev: T[]) => {
              const existingIds = new Set(
                prev.map((item) => getOptionValueRef.current(item)),
              );
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(getOptionValueRef.current(item)),
              );
              return [...prev, ...uniqueNewItems];
            };

            setOptions(dedupeAndAppend);
          } finally {
            setIsFetchingMore(false);
          }
        }
      };

      listElement.addEventListener("scroll", handleScroll);

      (listRef as any)._scrollHandler = handleScroll;
    });

    return () => {
      cancelAnimationFrame(rafId);
      const listElement = listRef.current;
      const handler = (listRef as any)._scrollHandler;
      if (listElement && handler) {
        listElement.removeEventListener("scroll", handler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onFetchMore, hasMore, isFetchingMore, reachedEnd, searchTerm]);

  const defaultRenderOption = (option: T) => {
    const label = getOptionLabel?.(option) ?? "";
    const subLabel = getOptionSubLabel?.(option);

    return (
      <div className="w-full flex flex-row gap-1 min-w-0 overflow-hidden">
        <div className="flex flex-row gap-1 shrink-0">
          <span className="text-muted-foreground">{subLabel}</span>
          {subLabel && <span className="text-muted-foreground">-</span>}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <span className="block truncate">{label}</span>
        </div>
      </div>
    );
  };

  const optionRenderer = renderOption ?? defaultRenderOption;

  const defaultRenderDisplayValue = (option: T) => {
    const label = getOptionLabel?.(option) ?? "";
    const subLabel = getOptionSubLabel?.(option);

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex flex-row gap-2">
          <span className="text-ellipsis overflow-hidden text-left">
            {label}
          </span>
        </div>
        <div className="flex w-full text-gray-500 text-xs overflow-hidden">
          <span className="flex items-center gap-2">{subLabel ?? null}</span>
        </div>
      </div>
    );
  };

  const displayValueRenderer = getDisplayValue ?? defaultRenderDisplayValue;

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (!readOnly && !disabled) {
          setOpen(newOpen);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant={variant}
          role="combobox"
          autoFocus={autoFocus}
          aria-expanded={open}
          data-combobox-id={comboBoxId}
          className={cn(
            "justify-between",
            disabled && "opacity-50 cursor-not-allowed",
            readOnly && "cursor-default",
            open &&
              "outline outline-2 outline-muted-foreground border-transparent outline-offset-[-2px]",
            triggerClassName,
          )}
          style={{ width: width }}
          disabled={disabled}
          readOnly={readOnly}
          aria-readonly={readOnly}
        >
          {selectedOption ? (
            displayValueRenderer(selectedOption)
          ) : loading && value ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-muted-foreground">{placeholder}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          {!readOnly && (
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{
          width: "var(--radix-popover-trigger-width)",
          minWidth: typeof width === "number" ? `${width}px` : width,
          maxWidth: "var(--radix-popover-trigger-width)",
        }}
        className={cn("p-0 min-w-[300px] overflow-hidden", className)}
        align="start"
      >
        <Command
          shouldFilter={false}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
        >
          <div className="relative border-b-1 w-full">
            <CommandInput
              ref={inputRef}
              placeholder={`${t("search")} ...`}
              data-combobox-id={comboBoxId}
              value={searchTerm}
              onValueChange={(value) => {
                setSearchTerm(value);
              }}
              onKeyDown={handleKeyDown}
            />
            {(loading || isSearching) && options.length > 0 && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>
          <CommandList ref={listRef} style={{ overflowAnchor: "none" }}>
            {error && (
              <div className="p-4 text-destructive text-center">{error}</div>
            )}
            {loading &&
              options.length === 0 &&
              (loadingSkeleton || <DefaultLoadingSkeleton />)}
            {!loading &&
              !error &&
              options.length === 0 &&
              (notFound || (
                <CommandEmpty className="py-2">
                  {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                </CommandEmpty>
              ))}
            <CommandItem value="-" className="hidden" />
            <CommandGroup>
              {options.map((option) => {
                const optionValue = getOptionValue(option);

                return (
                  <CommandItem
                    key={optionValue}
                    value={optionValue}
                    onSelect={handleSelect}
                    className={cn(
                      "group relative",
                      selectedValue === optionValue && "bg-accent/50",
                    )}
                  >
                    <div className="flex-1 min-w-0 overflow-hidden pr-6 group-aria-selected:pr-14">
                      {optionRenderer(option)}
                    </div>
                    {selectedValue === optionValue && (
                      <Check className="absolute right-2 h-4 w-4 text-primary" />
                    )}
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-aria-selected:opacity-100 transition-opacity duration-150">
                      <div className="flex items-center gap-1 bg-popover/95 backdrop-blur-sm border rounded px-1.5 py-1 shadow-sm">
                        <span className="text-[10px] capitalize leading-none">
                          {t("enter")}
                        </span>
                        <Shortcut.ShortcutEnter
                          size="sm"
                          className="bg-transparent p-0"
                        />
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
              <LoadMoreIndicator
                isLoading={isFetchingMore}
                hasMore={hasMore && !reachedEnd}
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DefaultLoadingSkeleton() {
  return (
    <CommandGroup>
      {[1, 2, 3].map((i) => (
        <CommandItem key={i} disabled>
          <div className="flex items-center gap-2 w-full">
            <div className="h-6 w-6 rounded-full animate-pulse bg-muted" />
            <div className="flex flex-col flex-1 gap-1">
              <div className="h-4 w-24 animate-pulse bg-muted rounded" />
              <div className="h-3 w-16 animate-pulse bg-muted rounded" />
            </div>
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function LoadMoreIndicator({
  isLoading,
  hasMore,
}: {
  isLoading: boolean;
  hasMore: boolean;
}) {
  if (!isLoading && !hasMore) return null;
  if (!isLoading) return null;

  return (
    <div className="py-2 space-y-1">
      {[1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-sm">
          <div className="h-4 w-4 rounded bg-muted/60 animate-pulse" />
          <div className="flex-1">
            <div
              className="h-3 bg-muted/60 rounded animate-pulse"
              style={{ width: `${60 + i * 15}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
